import React, { useState, useEffect } from 'react';
import { SITE_INFO } from '../data/siteData';

export default function ContactForm({
  title = 'kontaktformular',
  subtitle = 'Jeg bekræfter din henvendelse hurtigst muligt.',
  compact = false,
  className = '',
  delay = 60,
} = {}) {
  const [isVisible, setIsVisible] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) {
      setIsVisible(true);
      return;
    }
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);
  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    telefon: '',
    dato: '',
    beskrivelse: '',
    website_url: '', // Honeypot field for antispam
  });

  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = 'Venligst indtast dit navn';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Venligst indtast en gyldig emailadresse';
    }
    if (!formData.telefon.trim()) {
      errs.telefon = 'Venligst indtast dit telefonnummer';
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = 'Venligst skriv en kort beskrivelse eller besked';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.navn,
          email: formData.email,
          telefon: formData.telefon,
          dato: formData.dato,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse. Jeg bekræfter din henvendelse hurtigst muligt pr. email eller telefon.',
        });
        setFormData({
          navn: '',
          email: '',
          telefon: '',
          dato: '',
          beskrivelse: '',
          website_url: '',
        });
      } else {
        setStatus({
          type: 'error',
          message: 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller kontakt mig direkte på telefon 20 99 21 49.',
        });
      }
    } catch {
      setStatus({
        type: 'error',
        message: 'Netværksfejl. Tjek din forbindelse eller ring til mig på 20 99 21 49.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`relative overflow-hidden bg-[#243425]/90 backdrop-blur-md rounded-2xl transition-all duration-300 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      } ${
        compact
          ? 'p-4 sm:p-6 shadow-[0_18px_45px_rgba(20,30,21,0.22)] border border-[#3C523D]/60'
          : 'p-6 sm:p-8 border border-[#3C523D]/60 shadow-md'
      } ${className}`}
    >
      {/* Watermark Logo */}
      <img
        src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/1d30bca3-a748-4ede-8335-828014d68a29/ikon-logo.png"
        alt=""
        aria-hidden="true"
        className="absolute -right-6 -bottom-6 w-44 sm:w-52 opacity-[0.12] brightness-0 invert pointer-events-none select-none"
      />
      {(title || subtitle) && (
        <div className={compact ? 'mb-3 sm:mb-4' : 'mb-5'}>
          {title && (
            <h3 className={`font-serif ${compact ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'} text-white font-medium tracking-tight`}>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-xs text-[#C6D5C5] mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {status.type === 'success' ? (
        <div className="p-5 rounded-xl bg-[#E8F0E7] border border-[#C5DDC3] text-[#244226] text-center space-y-2.5 animate-in fade-in">
          <div className="text-[#3A4F3D] mx-auto text-2xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="font-serif text-lg font-medium">Henvendelse modtaget</h4>
          <p className="text-xs sm:text-sm leading-relaxed">{status.message}</p>
          <button
            type="button"
            onClick={() => setStatus({ type: null, message: '' })}
            className="inline-flex items-center text-xs font-semibold text-[#3A4F3D] underline hover:text-[#233424] mt-1 cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-3.5" noValidate>
          {/* Honeypot field - absolute hidden for humans, mandatory for antispam */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status.type === 'error' && (
            <div className="p-3 rounded-lg bg-red-950/60 border border-red-800 text-red-200 text-xs">
              <i className="fa-solid fa-circle-exclamation mr-1.5" aria-hidden="true" />
              {status.message}
            </div>
          )}

          {/* Name Field */}
          <div>
            <label htmlFor="navn" className="block text-[11px] font-medium uppercase tracking-wider text-[#C2D0C1] mb-0.5">
              Dit navn <span className="text-[#E7A684]">*</span>
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              value={formData.navn}
              onChange={handleChange}
              className={`w-full bg-transparent border-0 border-b border-white/30 focus:border-white px-0 py-1.5 text-white text-sm focus:outline-none transition-colors rounded-none ${
                errors.navn ? '!border-red-400' : ''
              }`}
            />
            {errors.navn && <p className="text-[11px] text-red-300 mt-0.5">{errors.navn}</p>}
          </div>

          {/* Grid for Email & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="email" className="block text-[11px] font-medium uppercase tracking-wider text-[#C2D0C1] mb-0.5">
                Email <span className="text-[#E7A684]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b border-white/30 focus:border-white px-0 py-1.5 text-white text-sm focus:outline-none transition-colors rounded-none ${
                  errors.email ? '!border-red-400' : ''
                }`}
              />
              {errors.email && <p className="text-[11px] text-red-300 mt-0.5">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="telefon" className="block text-[11px] font-medium uppercase tracking-wider text-[#C2D0C1] mb-0.5">
                Telefon <span className="text-[#E7A684]">*</span>
              </label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                value={formData.telefon}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b border-white/30 focus:border-white px-0 py-1.5 text-white text-sm focus:outline-none transition-colors rounded-none ${
                  errors.telefon ? '!border-red-400' : ''
                }`}
              />
              {errors.telefon && <p className="text-[11px] text-red-300 mt-0.5">{errors.telefon}</p>}
            </div>
          </div>

          {/* Optional Date / Preference */}
          <div>
            <label htmlFor="dato" className="block text-[11px] font-medium uppercase tracking-wider text-[#C2D0C1] mb-0.5">
              Ønsket dato eller tidspunkt (valgfrit)
            </label>
            <input
              type="text"
              id="dato"
              name="dato"
              value={formData.dato}
              onChange={handleChange}
              className="w-full bg-transparent border-0 border-b border-white/30 focus:border-white px-0 py-1.5 text-white text-sm focus:outline-none transition-colors rounded-none"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="beskrivelse" className="block text-[11px] font-medium uppercase tracking-wider text-[#C2D0C1] mb-0.5">
              Bemærkning / Beskrivelse <span className="text-[#E7A684]">*</span>
            </label>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              rows={compact ? 2 : 3}
              value={formData.beskrivelse}
              onChange={handleChange}
              className={`w-full bg-transparent border-0 border-b border-white/30 focus:border-white px-0 py-1.5 text-white text-sm focus:outline-none transition-colors rounded-none resize-y ${
                errors.beskrivelse ? '!border-red-400' : ''
              }`}
            />
            {errors.beskrivelse && <p className="text-[11px] text-red-300 mt-0.5">{errors.beskrivelse}</p>}
          </div>

          {/* Submit CTA */}
          <div className="pt-1.5">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-5 rounded-full text-sm font-semibold text-[#233424] bg-white hover:bg-[#FAF7F2] shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 group hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" />
                  <span>Sender henvendelse...</span>
                </>
              ) : (
                <>
                  <span className="whitespace-nowrap">Send til Kerneviden</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#233424] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </>
              )}
            </button>
            <p className="text-center text-[11px] text-[#A8BCA7] mt-2">
              Uforpligtende indledende samtale.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
